import { Component, computed, effect, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { PriceLevel, User } from 'depoto-core/src/entities'
import { ExportsComponent } from '../exports/exports.component'
import { customersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { CustomerService } from '../../services/entity-services/customer.service'

const DEFAULT_FILTERS = {
  fulltext: '',
  priceLevel: null,
  tags: [],
  tagsNegate: [],
  salesman: null,
}

@Component({
  selector: 'app-customers',
  templateUrl: 'customers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomersComponent extends BaseListing implements OnInit {
  private customerService = inject(CustomerService)
  private tagService = inject(TagService)

  @ViewChild('exportsModal') exportsModal: ExportsComponent

  listingColumns = signal(customersColumns)

  sortProp = signal('lastName')
  sortDir = signal('asc')

  filters = signal(this.loadViewSettings('customers')?.filters || structuredClone(DEFAULT_FILTERS))

  salesmen: User[] = []
  priceLevels: PriceLevel[] = []

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'customer'),
  }))

  readonly queryFilters = computed(() => {
    const filtersWithValues = {}
    for (const [key, value] of Object.entries(this.filters())) {
      // if it has value
      if (
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== 0 &&
        value !== '' &&
        value !== 'all' &&
        (!Array.isArray(value) || (Array.isArray(value) && value?.length))
      ) {
        if (key === 'salesman' && !!value) {
          filtersWithValues['user'] = Number(value)
        } else if (value === 'yes' || value === 'no') {
          // boolean
          filtersWithValues[key] = value === 'yes'
        } else if (Array.isArray(value) && value[0]?.id) {
          // array
          filtersWithValues[key] = value.map(object => object?.id)
        } else {
          // other
          filtersWithValues[key] = value
        }
      }
    }
    return filtersWithValues
  })

  readonly queryCustomers = injectInfiniteQuery(() => ({
    queryKey: ['customers', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      const params = {
        page: pageParam,
        sort: this.sortProp(),
        direction: this.sortDir(),
        filters: this.queryFilters(),
      }
      return await this.customerService
        .getCustomers(params, {
          id: null,
          firstName: null,
          lastName: null,
          companyName: null,
          name: null,
          email: null,
          phone: null,
          tags: { id: null, name: null },
          users: { id: null, name: null },
          priceLevel: { id: null, name: null },
        })
        .then(result => {
          this.saveViewSettings('customers', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryCustomers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  areCoreDataLoading = computed(() => {
    return this.queryTags.isFetching()
  })

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryCustomers.data()
      this.queryTags.data()
    })
  }

  ngOnInit() {
    this.loadSorting('customers')
    // `filters` is already restored from storage in its field initializer above; sortProp/sortDir
    // aren't, so restore them here synchronously. Deliberately not gated behind loadFilterOptions()
    // (a network call), since resolving it after the user starts typing into a filter would
    // overwrite their input with the stored/default value.
    const view = this.loadViewSettings('customers')
    this.sortProp.set(view?.sortProp || 'lastName')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadFilterOptions()
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryCustomers.refetch()
  }

  async loadFilterOptions() {
    if (this.core.services.user.hasRole('ROLE_SALESMAN')) {
      this.salesmen = [this.core.services.user.user]
    } else {
      const list = await this.core.services.user.getList({}, { id: null, name: null }).then(result => result?.items)
      this.salesmen = [new User({ id: 0, name: 'all' }), ...(list ?? [])]
    }

    const pls = await this.core.services.priceLevel
      .getList({ page: 1, sort: 'id', direction: 'asc' }, { id: null, name: null })
      .then(res => res.items)

    this.priceLevels = [new PriceLevel({ id: 0, name: 'all' }), ...(pls ?? [])]
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }
}
